import { USER_ROLES, createUserSchema, nonEmptyString, type UserDto } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input, SelectMenu, SelectMenuItem } from '../../components/ui';
import { useEnumLabels, useErrorText, useT } from '../../i18n';
import { useCreateUser, useUpdateUser } from './api';

/** When editing, the login is fixed, so the edit form needs its own schema rather than the API's. */
const editUserFormSchema = z.object({
  login: z.string().optional(),
  name: nonEmptyString(120, 'Name'),
  role: z.enum(USER_ROLES),
});

interface UserValues {
  login: string;
  name: string;
  role: (typeof USER_ROLES)[number];
}

export const UserFormDialog = ({
  open,
  onOpenChange,
  user,
  initialLogin = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserDto;
  /** Granting access: the GitLab login the admin was searching for. */
  initialLogin?: string;
}) => {
  const { t } = useT();
  const { role: roleLabel } = useEnumLabels();
  const { errorText, applyApiError } = useErrorText();
  const isEdit = user !== undefined;
  const create = useCreateUser();
  const update = useUpdateUser();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UserValues>({
    resolver: zodResolver(isEdit ? editUserFormSchema : createUserSchema),
    defaultValues: { login: '', name: '', role: 'qa_engineer' },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      login: user?.login ?? initialLogin,
      name: user?.name ?? '',
      role: user?.role ?? 'qa_engineer',
    });
  }, [open, user, initialLogin, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await update.mutateAsync({
          userId: user.id,
          input: { name: values.name, role: values.role },
        });
        toast.success(t('users.updated'));
      } else {
        await create.mutateAsync({
          login: values.login,
          ...(values.name ? { name: values.name } : {}),
          role: values.role,
        });
        toast.success(t('users.created', { login: values.login }));
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['login', 'name', 'role'])) {
        toast.error(errorText(error, 'users.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('users.editTitle', { login: user.login }) : t('users.new')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('users.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field
          label={t('users.fieldGitlabLogin')}
          error={errors.login?.message}
          hint={isEdit ? undefined : t('users.gitlabLoginHint')}
          required={!isEdit}
        >
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              disabled={isEdit}
              autoFocus={!isEdit}
              {...register('login')}
            />
          )}
        </Field>

        <Field
          label={t('common.name')}
          error={errors.name?.message}
          hint={isEdit ? undefined : t('users.nameHint')}
          required={isEdit}
        >
          {({ id, invalid, describedBy }) => (
            <Input id={id} invalid={invalid} aria-describedby={describedBy} {...register('name')} />
          )}
        </Field>

        <Field label={t('users.fieldRole')} error={errors.role?.message}>
          {({ id, invalid }) => (
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <SelectMenu
                  id={id}
                  invalid={invalid}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  {USER_ROLES.map((role) => (
                    <SelectMenuItem key={role} value={role}>
                      {roleLabel(role)}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
